import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type { Product, ActiveFilters } from '@/types'

interface UseProductsOptions {
  categorySlug?: string
  filters?: ActiveFilters
  enabled?: boolean
}

export function useProducts(options: UseProductsOptions = {}) {
  const { categorySlug, filters, enabled = true } = options

  return useQuery({
    queryKey: ['products', categorySlug, filters],
    queryFn: async () => {
      let query = supabase
        .from('products')
        .select(`
          *,
          category:categories(*),
          images:product_images(*),
          features:product_features(*),
          filters:product_filters(*)
        `)
        .eq('is_active', true)
        .order('sort_order', { ascending: true })
        .order('sort_order', { referencedTable: 'product_images', ascending: true })
        .order('sort_order', { referencedTable: 'product_features', ascending: true })

      // Filter by category
      if (categorySlug) {
        const { data: category } = await supabase
          .from('categories')
          .select('id')
          .eq('slug', categorySlug)
          .single()

        if (category) {
          query = query.eq('category_id', category.id)
        }
      }

      const { data, error } = await query

      if (error) throw error

      let products = data as Product[]

      // Apply client-side filtering for product_filters
      if (filters) {
        products = products.filter((product) => {
          const productFilters = product.filters || []

          // Check water_tightness filter
          if (filters.water_tightness.length > 0) {
            const hasMatch = productFilters.some(
              (f) =>
                f.filter_type === 'water_tightness' &&
                filters.water_tightness.includes(f.filter_value)
            )
            if (!hasMatch) return false
          }

          // Check fire_class filter
          if (filters.fire_class.length > 0) {
            const hasMatch = productFilters.some(
              (f) =>
                f.filter_type === 'fire_class' &&
                filters.fire_class.includes(f.filter_value)
            )
            if (!hasMatch) return false
          }

          // Check material filter
          if (filters.material.length > 0) {
            const hasMatch = productFilters.some(
              (f) =>
                f.filter_type === 'material' &&
                filters.material.includes(f.filter_value)
            )
            if (!hasMatch) return false
          }

          // Check product filter
          if (filters.product.length > 0) {
            const hasMatch = productFilters.some(
              (f) =>
                f.filter_type === 'product' &&
                filters.product.includes(f.filter_value)
            )
            if (!hasMatch) return false
          }

          return true
        })
      }

      return products
    },
    enabled,
  })
}

export function useProduct(slug: string | undefined) {
  return useQuery({
    queryKey: ['product', slug],
    queryFn: async () => {
      if (!slug) return null

      const { data, error } = await supabase
        .from('products')
        .select(`
          *,
          category:categories(*),
          images:product_images(*),
          features:product_features(*),
          filters:product_filters(*),
          options:product_options(*),
          certifications:product_certifications(
            certification:certifications(*)
          ),
          documents:documents(*)
        `)
        .eq('slug', slug)
        .eq('is_active', true)
        .order('sort_order', { referencedTable: 'product_images', ascending: true })
        .order('sort_order', { referencedTable: 'product_features', ascending: true })
        .single()

      if (error) throw error

      // Flatten certifications
      const product = {
        ...data,
        certifications: data.certifications?.map((pc: { certification: unknown }) => pc.certification) || [],
      } as Product

      return product
    },
    enabled: !!slug,
  })
}
